import React, { useState, useEffect, useRef } from 'react';
import Landmark from './Landmark';
import AnimatedPopulation from './map/AnimatedPopulation';
import LeaderArrow from './onboarding/LeaderArrow';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';
import { getRelationship, ONBOARDING_LAST_TURN } from '../config/onboarding';


/**
 * MapView2D - Main 2D World Map Component
 * Renders interactive world map with leaders, resources, animations, and strategic locations
 * 
 * Key Features:
 * - Dynamic leader emotions based on game actions
 * - Animated population counters
 * - Propaganda, diplomacy, blackmail, and nuclear strike animations
 * - Strategic landmarks (Strait of Hormuz, etc.)
 * - Victory stars display for eliminated enemies
 * - Camera pan/zoom (currently disabled)
 * 
 * @param {Object[]} countries - Array of country objects with resources and state
 * @param {string|null} selectedCountry - Currently selected country code
 * @param {Function} onCountryClick - Handler for country click events
 * @param {Function} onCountryHover - Handler for country hover events
 * @param {Function} onCountryLeave - Handler for country leave events
 * @param {Object|null} lastAction - Last action performed (for emotion updates)
 * @param {boolean} isActionModalOpen - Whether action result modal is open
 * @param {Object|null} propagandaAnimation - Active propaganda animation state
 * @param {string|null} hiddenLeaderCode - Country code of hidden leader (during nuke)
 * @param {string[]} sweatingLeaders - Array of country codes with sweating leaders
 * @param {string[]} defeatedCountries - Array of defeated country codes
 * @param {string|null} activeCountryCode - Currently active country during AI turns
 * @param {boolean} nuclearStrikeActive - Whether nuclear strike is in progress
 * @param {string|null} nuclearStrikeTargetCode - Target country of nuclear strike
 * @param {boolean} airStrikeActive - Whether air strike is in progress
 * @param {number} currentTurn - Current turn number
 * @param {Object} game - Complete game state object
 * @param {Function} setGame - Setter for game state
 */
export default function MapView2D({ countries, selectedCountry, onCountryClick, onCountryHover, onCountryLeave, lastAction, isActionModalOpen, propagandaAnimation, hiddenLeaderCode, sweatingLeaders, defeatedCountries = [], activeCountryCode, nuclearStrikeActive, nuclearStrikeTargetCode, airStrikeActive, currentTurn, isProcessingAiTurns, game, setGame }) {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [countryEmotions, setCountryEmotions] = useState({});
  const [persistentEmotions, setPersistentEmotions] = useState({}); // Track which countries are angry/scared
  const [animationInterval, setAnimationInterval] = useState(null);
  // CAMERA CONTROLS DISABLED - causing issues with air strikes
  // const [cameraPan, setCameraPan] = useState({ x: 0, y: 0 });
  // const [cameraZoom, setCameraZoom] = useState(1.0);
  const cameraPan = { x: 0, y: 0 };  // Fixed position
  const cameraZoom = 1.0;  // Fixed zoom
  const isInitialMount = useRef(true); // Track initial mount to prevent pan on load
  const propagandaAudioRef = useRef(null); // Track propaganda audio for cleanup
  const [testDeploymentIndex, setTestDeploymentIndex] = useState(0); // Test button state

  // ============================================================
  // 📱 MOBILE AUTO-SCALE — Shrinks country markers, leaders, landmarks,
  // and deployed troops so they fit on phone/tablet screens.
  // Desktop (≥1024px wide): no change (scale = 1)
  // Tablet (768–1023px):    scale = 0.75
  // Phone (<768px):         scale = 0.55
  // The world map background is NOT scaled — only the entities on top of it.
  // ============================================================
  const [mapEntityScale, setMapEntityScale] = useState(1);
  useEffect(() => {
    const computeScale = () => {
      const w = window.innerWidth;
      if (w < 768) setMapEntityScale(0.55);
      else if (w < 1024) setMapEntityScale(0.75);
      else setMapEntityScale(1);
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    window.addEventListener('orientationchange', computeScale);
    return () => {
      window.removeEventListener('resize', computeScale);
      window.removeEventListener('orientationchange', computeScale);
    };
  }, []);
  
  // Shuffled propaganda playlists for each country (like blackmail system)
  const [propagandaPlaylists, setPropagandaPlaylists] = useState({
    USA: { playlist: [], index: 0 },
    EU: { playlist: [], index: 0 },
    RUS: { playlist: [], index: 0 },
    CHN: { playlist: [], index: 0 },
    NK: { playlist: [], index: 0 }
  });
  
  // Shuffled sprite playlists for propaganda visuals
  const [propagandaSpritePlaylists, setPropagandaSpritePlaylists] = useState({
    USA: { playlist: [], index: 0 },
    EU: { playlist: [], index: 0 },
    RUS: { playlist: [], index: 0 },
    CHN: { playlist: [], index: 0 },
    NK: { playlist: [], index: 0 }
  });

  // Get propaganda sprite filename (with SHUFFLED PLAYLIST - no repeats!)
  const getPropagandaSprite = (countryCode) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    
    // Define all available sprite variants for each country
    const spriteVariants = {
      'USA': [1, 2, 3, 4], // 4 propaganda sprites
      'RUS': [1, 2, 3],    // 3 propaganda sprites
      'CHN': [1, 2, 3],    // 3 propaganda sprites
      'EU': [1, 2, 3],     // 3 propaganda sprites
      'NK': [1, 2, 3]      // 3 propaganda sprites
    };
    
    const variants = spriteVariants[countryCode];
    if (!variants) {
      // Default fallback for countries without variants
      return `/assets/leaders/propaganda/${spriteName}-propaganda.png`;
    }
    
    // Get current playlist for this country (READ ONLY - no state updates during render!)
    let currentPlaylist = propagandaSpritePlaylists[countryCode];
    
    // Initialize or reshuffle if playlist is empty/complete
    if (!currentPlaylist.playlist.length || currentPlaylist.index >= currentPlaylist.playlist.length) {
      const shuffled = [...variants].sort(() => Math.random() - 0.5);
      currentPlaylist = { playlist: shuffled, index: 0 };
    }
    
    // Get next sprite variant from playlist (just read, don't update yet)
    const variantNumber = currentPlaylist.playlist[currentPlaylist.index];
    
    // Return the sprite path
    if (variantNumber === 1) {
      return `/assets/leaders/propaganda/${spriteName}-propaganda.png`;
    } else {
      return `/assets/leaders/propaganda/${spriteName}-propaganda-${variantNumber}.png`;
    }
  };

  // Calculate if propaganda sprite should be flipped
  const shouldFlipPropaganda = (sourceCode, targetCode) => {
    const positions = {
      'USA': { x: 20 },
      'RUS': { x: 65 },
      'CHN': { x: 75 },
      'EU': { x: 48 },
      'NK': { x: 82 }
    };
    
    const sourcePos = positions[sourceCode];
    const targetPos = positions[targetCode];
    
    if (!sourcePos || !targetPos) return false;
    
    // If target is to the left, flip the sprite
    return targetPos.x < sourcePos.x;
  };

  // Handle emotion changes based on game events with looping
  useEffect(() => {
    if (lastAction && lastAction.result) {
      const { source, target, success, action } = lastAction.result;
      
      // Determine emotions based on action type and success/failure
      let sourceEmotion = success ? 'happy' : 'scared';
      let targetEmotion = success ? 'scared' : 'happy';
      
      // Show ANGRY for aggressive actions that succeed!
      const aggressiveActions = ['air_strike', 'deploy_troops', 'blackmail', 'propaganda'];
      if (success && aggressiveActions.includes(action)) {
        targetEmotion = 'angry'; // Target is ANGRY when successfully attacked!
        
        // Set persistent emotion (stays until next turn)
        setPersistentEmotions(prev => ({
          ...prev,
          [target]: {
            emotion: 'angry',
            expiresAt: currentTurn + 2 // Stay angry for 2 turns
          }
        }));
      }
      
      // Exception: Nuclear strikes are too devastating - target is SCARED not angry
      if (action === 'nuclear_strike' && success) {
        targetEmotion = 'scared';
        
        // Set persistent emotion for nuked country
        setPersistentEmotions(prev => ({
          ...prev,
          [target]: {
            emotion: 'scared',
            expiresAt: currentTurn + 3 // Stay scared for 3 turns
          }
        }));
      }
      
      // Set initial emotions (for modal animation)
      setCountryEmotions({
        [source]: sourceEmotion,
        [target]: targetEmotion
      });
      
      // Clear any existing interval
      if (animationInterval) {
        clearInterval(animationInterval);
      }
      
      // If modal is open, loop the animations
      if (isActionModalOpen) {
        let isEmotional = true;
        const interval = setInterval(() => {
          if (isEmotional) {
            // Show normal
            setCountryEmotions({
              [source]: 'normal',
              [target]: 'normal'
            });
          } else {
            // Show emotion
            setCountryEmotions({
              [source]: sourceEmotion,
              [target]: targetEmotion
            });
          }
          isEmotional = !isEmotional;
        }, 2000); // Toggle every 2 seconds
        
        setAnimationInterval(interval);
      }
    }
  }, [lastAction, isActionModalOpen]);
  
  // Clean up when modal closes
  useEffect(() => {
    if (!isActionModalOpen && animationInterval) {
      clearInterval(animationInterval);
      setAnimationInterval(null);
      // Don't reset emotions - let persistent emotions show
      setTimeout(() => {
        setCountryEmotions({});
      }, 500);
    }
  }, [isActionModalOpen, animationInterval]);
  
  // Clean up expired persistent emotions each turn
  useEffect(() => {
    const newPersistentEmotions = { ...persistentEmotions };
    let hasChanges = false;
    
    Object.keys(newPersistentEmotions).forEach(countryCode => {
      if (newPersistentEmotions[countryCode].expiresAt <= currentTurn) {
        delete newPersistentEmotions[countryCode];
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setPersistentEmotions(newPersistentEmotions);
    }
  }, [currentTurn]);

  // Apply persistent emotions when not in modal
  useEffect(() => {
    if (!isActionModalOpen) {
      const emotionsToShow = {};
      Object.keys(persistentEmotions).forEach(countryCode => {
        emotionsToShow[countryCode] = persistentEmotions[countryCode].emotion;
      });
      setCountryEmotions(emotionsToShow);
    }
  }, [persistentEmotions, isActionModalOpen]);

  // NEW: Idle emotions - Leaders show random emotions when nothing is happening
  // This makes the beautiful emotional sprites visible during regular gameplay
  useEffect(() => {
    // Only show idle emotions when:
    // 1. Not in action modal
    // 2. No persistent emotions for that country
    // 3. Not during propaganda animation
    if (!isActionModalOpen && !propagandaAnimation) {
      const idleEmotionInterval = setInterval(() => {
        const emotionsToShow = { ...countryEmotions };
        
        countries.forEach(country => {
          const code = country.code;
          
          // Skip if country has persistent emotion
          if (persistentEmotions[code]) return;
          
          // 30% chance to show an emotion
          if (Math.random() < 0.3) {
            // Random emotion weighted towards happy/neutral gameplay
            const rand = Math.random();
            if (rand < 0.4) {
              emotionsToShow[code] = 'happy'; // 40% happy
            } else if (rand < 0.65) {
              emotionsToShow[code] = 'angry'; // 25% angry (show those sprites!)
            } else if (rand < 0.85) {
              emotionsToShow[code] = 'scared'; // 20% scared
            } else {
              delete emotionsToShow[code]; // 15% neutral
            }
          } else {
            // Clear emotion (neutral face)
            delete emotionsToShow[code];
          }
        });
        
        setCountryEmotions(emotionsToShow);
      }, 4000); // Change emotions every 4 seconds
      
      return () => clearInterval(idleEmotionInterval);
    }
  }, [isActionModalOpen, propagandaAnimation, countries, persistentEmotions]);

  // Play country-specific propaganda sounds
  // 🔈 Audio channel: claim/release around propaganda scenes so iOS hands
  // us the channel and the music doesn't drown out the speeches.
  useEffect(() => {
    if (!propagandaAnimation) return;
    claimAudioChannel();
    return () => {
      releaseAudioChannel();
    };
  }, [!!propagandaAnimation]);

  useEffect(() => {
    // ✅ FIX: Don't interrupt audio that's currently playing
    // Only cleanup if audio has finished or if we're explicitly ending propaganda phase
    const currentAudio = propagandaAudioRef.current;
    const isCurrentlyPlaying = currentAudio && !currentAudio.paused && !currentAudio.ended;
    
    // If there's audio playing and we're starting a NEW propaganda, skip to avoid interruption
    if (isCurrentlyPlaying && propagandaAnimation && propagandaAnimation.phase === 'propaganda') {
      console.log(`🎵 Audio already playing - skipping this propaganda sound to avoid interruption`);
      return;
    }
    
    // Cleanup previous audio ONLY if it's not playing
    if (currentAudio && !isCurrentlyPlaying) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      propagandaAudioRef.current = null;
    }

    if (propagandaAnimation && propagandaAnimation.phase === 'propaganda') {
      const countryCode = propagandaAnimation.source;
      
      // Update sprite playlist index OUTSIDE of render (in useEffect)
      const spriteVariants = {
        'USA': [1, 2, 3, 4],
        'RUS': [1, 2, 3],
        'CHN': [1, 2, 3],
        'EU': [1, 2, 3],
        'NK': [1, 2, 3]
      };
      
      if (spriteVariants[countryCode]) {
        let currentPlaylist = propagandaSpritePlaylists[countryCode];
        
        if (!currentPlaylist.playlist.length || currentPlaylist.index >= currentPlaylist.playlist.length) {
          const shuffled = [...spriteVariants[countryCode]].sort(() => Math.random() - 0.5);
          currentPlaylist = { playlist: shuffled, index: 0 };
          console.log(`🎨 Shuffled ${countryCode} propaganda sprites (${shuffled.length} variants)`);
        }
        
        console.log(`🎨 ${countryCode} propaganda sprite ${currentPlaylist.index + 1}/${currentPlaylist.playlist.length}: variant ${currentPlaylist.playlist[currentPlaylist.index]}`);
        
        // NOW update the index (safe inside useEffect)
        setPropagandaSpritePlaylists(prev => ({
          ...prev,
          [countryCode]: {
            ...currentPlaylist,
            index: currentPlaylist.index + 1
          }
        }));
      }
      
      // Define all propaganda sounds for each country
      const allPropagandaSounds = {
        USA: [
          '/assets/audio/usa-propaganda-1.mp3',
          '/assets/audio/usa-propaganda-2-new.mp3',
          '/assets/audio/usa-propaganda-3.mp3',
          '/assets/audio/usa-propaganda-4.mp3',
          '/assets/audio/usa-propaganda-6-wars.mp3'
        ],
        EU: [
          '/assets/audio/propaganda-eu-1.mp3',
          '/assets/audio/eu-propaganda-2.mp3'
        ],
        RUS: [
          '/assets/audio/russia-propaganda-1-europe-weapons.mp3',
          '/assets/audio/russia-propaganda-2-english.mp3',
          '/assets/audio/russia-propaganda-3-weapon.mp3'
        ],
        CHN: [
          '/assets/audio/china-propaganda-1.mp3',
          '/assets/audio/china-propaganda-2.mp3',
          '/assets/audio/china-propaganda-3.mp3',
          '/assets/audio/china-propaganda-1-anchor.mp3',
          '/assets/audio/china-propaganda-2-calm.mp3'
        ],
        NK: [
          '/assets/audio/nkorea-propaganda-1.mp3',
          '/assets/audio/nkorea-propaganda-2.mp3',
          '/assets/audio/nkorea-propaganda-3.mp3',
          '/assets/audio/nkorea-propaganda-4-kim1.mp3',
          '/assets/audio/nkorea-propaganda-5-us-aggression.mp3',
          '/assets/audio/nkorea-propaganda-6-kim4.mp3',
          '/assets/audio/nkorea-propaganda-7-kim5.mp3',
          '/assets/audio/nkorea-propaganda-8-kim-jong-il.mp3',
          '/assets/audio/nkorea-propaganda-9-nkprop2.mp3',
          '/assets/audio/nkorea-propaganda-10-nkprop.mp3'
        ]
      };
      
      if (!allPropagandaSounds[countryCode]) {
        console.log(`⚠️ No propaganda sounds for ${countryCode} yet`);
        return;
      }
      
      // Get or create shuffled playlist for this country
      let currentPlaylist = propagandaPlaylists[countryCode];
      
      // Initialize or reshuffle if playlist is empty/complete
      if (!currentPlaylist.playlist.length || currentPlaylist.index >= currentPlaylist.playlist.length) {
        const shuffled = [...allPropagandaSounds[countryCode]].sort(() => Math.random() - 0.5);
        currentPlaylist = { playlist: shuffled, index: 0 };
        console.log(`🔄 Shuffled ${countryCode} propaganda playlist (${shuffled.length} sounds)`);
      }
      
      // Get next sound from playlist
      const audioFile = currentPlaylist.playlist[currentPlaylist.index];
      console.log(`🎵 Playing ${countryCode} propaganda ${currentPlaylist.index + 1}/${currentPlaylist.playlist.length}: ${audioFile.split('/').pop()}`);
      
      // Update playlist state (increment index)
      setPropagandaPlaylists(prev => ({
        ...prev,
        [countryCode]: {
          ...currentPlaylist,
          index: currentPlaylist.index + 1
        }
      }));
      
      if (audioFile) {
        const audio = new Audio(audioFile);
        audio.volume = 1.0; // 100% volume (maximum allowed by HTML5 Audio API)
        audio.currentTime = 1.0; // Skip first 1 second (silence at beginning of audio files)
        propagandaAudioRef.current = audio; // Store reference for cleanup
        audio.play().catch(e => {
          console.error(`❌ ${propagandaAnimation.source} propaganda audio play failed:`, e);
          console.error(`❌ Attempted file: ${audioFile}`);
        });
      }
    }
        
    return () => {
      // Cleanup when component unmounts (NOT when propaganda changes)
      if (propagandaAudioRef.current) {
        const audio = propagandaAudioRef.current;
        // Only stop if audio has finished or we're unmounting completely
        if (audio.ended || !propagandaAnimation) {
          audio.pause();
          audio.currentTime = 0;
          propagandaAudioRef.current = null;
          console.log('🔇 Stopped propaganda audio (cleanup)');
        }
      }
    };
  }, [propagandaAnimation]);

  // CAMERA PAN & ZOOM FULLY DISABLED
  // All camera movement code removed to fix errors and improve air strike viewing
  // useEffect(() => { ... camera code removed ... }, [activeCountryCode]);
  // useEffect(() => { ... nuke camera code removed ... }, [nuclearStrikeActive]);

  // Get the appropriate sprite for a country based on its current emotion
  const getCountrySprite = (countryCode, emotion) => {
    // Map country codes to sprite filenames
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    const baseSprite = `/assets/leaders/${spriteName}.png`;
    
    // USA has 3 normal/casual sprites - changes ONCE PER ROUND based on turn number
    if (countryCode === 'USA' && (!emotion || emotion === 'normal')) {
      const turnNumber = currentTurn || 1;
      const variant = (turnNumber % 3) + 1; // Cycles 1, 2, 3 based on turn
      
      if (variant === 1) {
        return baseSprite; // Original formal suit
      } else {
        return `/assets/leaders/${spriteName}-normal-${variant}.png`; // Casual variants
      }
    }
    
    // Russia has 3 normal/casual sprites - changes ONCE PER TURN based on turn number
    if (countryCode === 'RUS' && (!emotion || emotion === 'normal')) {
      const turnNumber = currentTurn || 1;
      const variant = (turnNumber % 3) + 1; // Cycles 1, 2, 3 based on turn (removed variant 4)
      
      if (variant === 1) {
        return baseSprite; // Original formal uniform
      } else {
        return `/assets/leaders/${spriteName}-normal-${variant}.png`; // Casual variants
      }
    }
    
    // EU has 3 normal sprites - changes ONCE PER ROUND based on turn number
    if (countryCode === 'EU' && (!emotion || emotion === 'normal')) {
      const turnNumber = currentTurn || 1;
      const variant = (turnNumber % 3) + 1; // Cycles 1, 2, 3 based on turn
      
      if (variant === 1) {
        return baseSprite; // Original formal suit
      } else {
        return `/assets/leaders/${spriteName}-normal-${variant}.png`; // Casual variants
      }
    }
    
    // China has 2 normal sprites - changes ONCE PER ROUND based on turn number
    if (countryCode === 'CHN' && (!emotion || emotion === 'normal')) {
      const turnNumber = currentTurn || 1;
      const variant = (turnNumber % 2) + 1; // Cycles 1, 2 based on turn
      
      if (variant === 1) {
        return baseSprite; // Original formal suit
      } else {
        return `/assets/leaders/${spriteName}-normal-${variant}.png`; // Casual spy variant
      }
    }
    
    if (!emotion || emotion === 'normal') {
      return baseSprite;
    }
    
    // North Korea has 6 happy sprites - randomly select one!
    if (countryCode === 'NK' && emotion === 'happy') {
      const randomVariant = Math.floor(Math.random() * 6) + 1; // 1, 2, 3, 4, 5, or 6
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-happy.png`;
      } else {
        return `/assets/leaders/${spriteName}-happy-${randomVariant}.png`;
      }
    }
    
    // Russia has 3 happy sprites - randomly select one!
    if (countryCode === 'RUS' && emotion === 'happy') {
      const randomVariant = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-happy.png`;
      } else {
        return `/assets/leaders/${spriteName}-happy-${randomVariant}.png`;
      }
    }
    
    // USA has 5 happy sprites - randomly select one!
    if (countryCode === 'USA' && emotion === 'happy') {
      const randomVariant = Math.floor(Math.random() * 5) + 1; // 1, 2, 3, 4, or 5
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-happy.png`;
      } else {
        return `/assets/leaders/${spriteName}-happy-${randomVariant}.png`;
      }
    }
    
    // EU has 3 happy sprites - randomly select one!
    if (countryCode === 'EU' && emotion === 'happy') {
      const randomVariant = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-happy.png`;
      } else {
        return `/assets/leaders/${spriteName}-happy-${randomVariant}.png`;
      }
    }
    
    // China has 2 happy sprites - randomly select one!
    if (countryCode === 'CHN' && emotion === 'happy') {
      const randomVariant = Math.floor(Math.random() * 2) + 1; // 1 or 2
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-happy.png`;
      } else {
        return `/assets/leaders/${spriteName}-happy-${randomVariant}.png`;
      }
    }
    
    // USA has 3 scared sprites - randomly select one!
    if (countryCode === 'USA' && emotion === 'scared') {
      const randomVariant = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-scared.png`;
      } else {
        return `/assets/leaders/${spriteName}-scared-${randomVariant}.png`;
      }
    }
    
    // USA has 2 angry sprites - randomly select one!
    if (countryCode === 'USA' && emotion === 'angry') {
      const randomVariant = Math.floor(Math.random() * 2) + 1; // 1 or 2
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-angry.png`;
      } else {
        return `/assets/leaders/${spriteName}-angry-${randomVariant}.png`;
      }
    }
    
    // Russia has 3 angry sprites - randomly select one!
    if (countryCode === 'RUS' && emotion === 'angry') {
      const randomVariant = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-angry.png`;
      } else {
        return `/assets/leaders/${spriteName}-angry-${randomVariant}.png`;
      }
    }
    
    // EU has 2 angry sprites - randomly select one!
    if (countryCode === 'EU' && emotion === 'angry') {
      const randomVariant = Math.floor(Math.random() * 2) + 1; // 1 or 2
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-angry.png`;
      } else {
        return `/assets/leaders/${spriteName}-angry-${randomVariant}.png`;
      }
    }
    
    // China has 2 angry sprites - randomly select one!
    if (countryCode === 'CHN' && emotion === 'angry') {
      const randomVariant = Math.floor(Math.random() * 2) + 1; // 1 or 2
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-angry.png`;
      } else {
        return `/assets/leaders/${spriteName}-angry-${randomVariant}.png`;
      }
    }
    
    // North Korea has 4 angry sprites - randomly select one!
    if (countryCode === 'NK' && emotion === 'angry') {
      const randomVariant = Math.floor(Math.random() * 4) + 1; // 1, 2, 3, or 4
      if (randomVariant === 1) {
        return `/assets/leaders/${spriteName}-angry.png`;
      } else {
        return `/assets/leaders/${spriteName}-angry-${randomVariant}.png`;
      }
    }
    
    // Check if emotional sprite exists for this country
    const emotionalSprite = `/assets/leaders/${spriteName}-${emotion}.png`;
    return emotionalSprite;
  };

  // Country positions on the 2D map (as percentages of map dimensions)
  // Based on actual world geography
  const countryPositions = {
    'USA': { left: '20%', top: '35%', color: '#3b82f6' },      // North America
    'RUS': { left: '65%', top: '25%', color: '#dc2626' },      // Russia/Eastern Europe
    'CHN': { left: '75%', top: '40%', color: '#f59e0b' },      // East Asia
    'EU': { left: '48%', top: '30%', color: '#3b82f6' },       // Western Europe
    'NK': { left: '82%', top: '38%', color: '#7c2d12' }        // Korean Peninsula
  };

  // Leader sprite offsets to prevent overlap (pixels from center)
  const leaderOffsets = {
    'USA': { x: 0, y: 0 },       // USA is fine
    'RUS': { x: 0, y: 0 },       // Russia is fine
    'CHN': { x: -15, y: 15 },    // Move China slightly southwest - balanced between RUS and NK
    'EU': { x: 0, y: 0 },        // EU is fine
    'NK': { x: 50, y: -30 }      // Move NK northeast away from China
  };

  // Nuke sprite offsets - custom positioning for visibility
  // Positioned relative to territory circle center for each country
  // Adjusted to be ON the map territory, not near the leader sprites
  const nukeOffsets = {
    'USA': { x: -100, y: 96 },      // On USA territory (moved south from Canada)
    'RUS': { x: -80, y: 64 },       // On Russia territory (moved south)
    'CHN': { x: 44, y: 148 },       // On China territory (moved 1/2 inch NE)
    'EU': { x: 60, y: 148 },        // On EU territory (moved 1/2 inch east to avoid dots)
    'NK': { x: -2, y: 102 }         // On NK territory (moved SW)
  };

  const handleCountryClick = (country) => {
    if (onCountryClick) {
      onCountryClick(country);
    }
  };

  // 📱 Mobile portrait detection — when phone is held vertically,
  // the world map (2:1 ratio) gets crushed into the narrow viewport
  // and all countries cluster together. We render the map at its
  // natural aspect ratio (wider than viewport) and allow native
  // horizontal swipe-pan via overflow-x-auto. Landscape phones, tablets,
  // and all desktops continue to render the map full-screen as before.
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobilePortrait(w < 768 && h > w);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // 📱 Scroll container ref — used by the auto-pan effect below to bring
  // the active country into view on mobile portrait whenever a cinematic
  // (propaganda, diplomacy, blackmail, nuke, air strike) kicks off.
  const scrollContainerRef = useRef(null);

  // 📱 Auto-pan to country in action.
  // On mobile portrait the map is 200% wide and only ~50% is visible at any
  // time, so cinematics that play on the far side of the map are off-screen.
  // Whenever a new action animation starts, smoothly scroll the container
  // horizontally so the source country sits in the middle of the viewport.
  useEffect(() => {
    if (!isMobilePortrait) return;
    const el = scrollContainerRef.current;
    if (!el) return;

    // Figure out which country is "on stage"
    const activeCode =
      propagandaAnimation?.source ||
      activeCountryCode ||
      (nuclearStrikeActive && nuclearStrikeTargetCode) ||
      null;

    if (!activeCode) return;

    const pos = countryPositions[activeCode];
    if (!pos) return;

    // pos.left is a string like "20%". Map is 200% of viewport on mobile
    // portrait, so 20% of the map = (20/100) * (2 * clientWidth) =
    // 0.4 * clientWidth from the left edge of the scroll surface.
    const leftPercent = parseFloat(pos.left);
    const mapWidth = el.scrollWidth; // ~ 2 * clientWidth
    const targetX = (leftPercent / 100) * mapWidth - el.clientWidth / 2;
    const clampedX = Math.max(0, Math.min(targetX, mapWidth - el.clientWidth));

    el.scrollTo({ left: clampedX, behavior: 'smooth' });
    console.log(`📷 Auto-pan to ${activeCode} → scrollLeft ${clampedX.toFixed(0)}px`);
  }, [isMobilePortrait, propagandaAnimation, activeCountryCode, nuclearStrikeActive, nuclearStrikeTargetCode]);

  // 🎓 ONBOARDING — show arrows above leaders during turns 1-2, but only
  // when it's the player's turn (so they don't compete with AI cinematics).
  // Stays visible for ALL 3 actions of those turns so the player has time
  // to read it; auto-dismisses on turn 3+.
  const playerCountry = countries?.find(c => c.is_player);
  const playerCode = playerCountry?.code;
  const playerActionsRemaining = playerCountry?.actions_remaining ?? 0;
  const showOnboarding =
    !!playerCode &&
    currentTurn <= ONBOARDING_LAST_TURN &&
    playerActionsRemaining > 0 && // player still has actions this turn
    !isActionModalOpen &&
    !propagandaAnimation &&
    !nuclearStrikeActive;

  // 🔒 LOCKED-LANDMARK VISIBILITY — Hide Iran/Israel/Venezuela/Mexico/Hormuz/SA
  // any time it's not the player's turn or any AI cinematic is in progress.
  // Reasons it stays hidden:
  //   - AI turns being processed (isProcessingAiTurns)
  //   - Any AI cinematic playing (propaganda / nuclear / air strike)
  //   - Mobile portrait & player already spent at least one action
  // Reasons it shows:
  //   - Desktop and tablet during player's own turn (any actions remaining)
  //   - Mobile portrait at the START of the player's turn (3 actions)
  const showLockedLandmarks =
    !isProcessingAiTurns &&
    !propagandaAnimation &&
    !nuclearStrikeActive &&
    !airStrikeActive &&
    (!isMobilePortrait || playerActionsRemaining === 3);

  // 🎙️ ONBOARDING VOICEOVER — Play the "Welcome, Commander..." narration
  // once on turn 1, 2 seconds after the game starts. Plays only on the
  // FIRST mount of turn 1 so it doesn't repeat if React remounts the map.
  const welcomeVoicePlayedRef = useRef(false);
  useEffect(() => {
    if (welcomeVoicePlayedRef.current) return;
    if (currentTurn !== 1) return;
    if (!playerCode) return;

    welcomeVoicePlayedRef.current = true;
    const timer = setTimeout(() => {
      // Pause music for the narration (iOS audio channel) and resume after
      claimAudioChannel();
      const voice = new Audio('/assets/audio/welcome-commander.mp3');
      voice.volume = 0.9;
      voice.addEventListener('ended', releaseAudioChannel, { once: true });
      voice.addEventListener('error', releaseAudioChannel, { once: true });
      voice.play().catch(e => {
        console.log('Welcome voice failed:', e);
        releaseAudioChannel();
      });
      console.log('🎙️ Welcome, Commander narration playing');
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentTurn, playerCode]);

  return (
    <>
      <div 
        className={`relative w-full h-screen ${isMobilePortrait ? 'overflow-x-auto overflow-y-hidden pointer-events-auto' : 'overflow-hidden pointer-events-none'}`}
        style={isMobilePortrait ? { WebkitOverflowScrolling: 'touch', scrollSnapType: 'none', scrollBehavior: 'smooth' } : undefined}
        data-map-scroll-container="true"
        ref={(el) => {
          scrollContainerRef.current = el;
          // Auto-center the scroll on player's country once on mount
          // so they don't have to scroll just to find themselves
          if (el && isMobilePortrait && !el.dataset.centered) {
            requestAnimationFrame(() => {
              const target = (el.scrollWidth - el.clientWidth) / 2;
              el.scrollLeft = target;
              el.dataset.centered = '1';
            });
          }
        }}
      >
      {/* World Map Background - Full Screen with Camera Pan & Zoom.
          📱 On mobile portrait, push the entire stage down ~80px so the
          top-row countries (Russia, China, NK) aren't crammed against the
          screen edge. The empty top band picks up the parent's dark slate
          background, matching the game's aesthetic. */}
      <div 
        className="absolute inset-x-0 bottom-0 transition-transform duration-1000 ease-out"
        style={{
          top: isMobilePortrait ? '80px' : '0',
          transform: `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom})`,
          // On portrait phones, render the map at its natural 2:1 ratio
          // (200% viewport width) so countries spread out and don't
          // overlap each other.
          ...(isMobilePortrait ? { width: '200%', minWidth: '200%' } : {})
        }}
      >
        
        {/* High-Resolution World Map */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/world-map.png)',
            // On mobile portrait we stretch the image to 100% 100% to match
            // the 200%-wide container exactly, so the country position
            // percentages (USA at 20%, NK at 80%) align with actual
            // geography on the rendered image. `cover` was zooming + cropping
            // and shifting all the entity positions off-target.
            backgroundSize: isMobilePortrait ? '100% 100%' : 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.6,
            filter: 'brightness(0.7) contrast(1.2) saturate(0.9)',
          }}
        />
        
        {/* Dark overlay for better contrast with UI elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/20 to-slate-900/50" />

        {/* Subtle grid overlay for strategic game feel */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(#4a5568 1px, transparent 1px), linear-gradient(90deg, #4a5568 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />

        {/* Game Title - Top Left Corner. On mobile, pushed down to clear the
            Exit-to-Lobby button which now lives at top-4 left-4. */}
        <div className="absolute top-14 md:top-4 left-4 z-10 text-left">
          <h1 
            className="font-heading text-3xl md:text-4xl tracking-widest mb-1"
            style={{ 
              color: '#fbbf24',
              textShadow: '0 0 20px rgba(251, 191, 36, 0.5), 0 2px 4px rgba(0,0,0,0.8)',
              letterSpacing: '0.3em'
            }}
          >
            GLOBAL THERMO <span style={{ color: '#ef4444' }}>III</span>
          </h1>
          <p className="text-amber-200/80 text-sm tracking-wide" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
            Turn-Based Strategy
          </p>
        </div>

        {/* 📱 MOBILE AUTO-SCALE WRAPPER — Shrinks all on-map entities (countries, leaders, landmarks, troops) so they fit on phones/tablets.
            CRITICAL: This wrapper is INSIDE the camera div which is already
            200% wide on mobile portrait. So we must use width: 100% here
            (not 200%, which would compound to 400% and push leaders off-map).
            Also we skip the scale() on mobile portrait — the 200% scrollable
            canvas already gives entities room, and any scale with center
            origin would shift them off the map background. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: isMobilePortrait ? 'none' : `scale(${mapEntityScale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease',
          }}
        >

        {/* Countries as clickable regions */}
        {countries && countries
          .filter(country => !defeatedCountries.includes(country.code))
          .map((country) => {
            const pos = countryPositions[country.code];
            if (!pos) return null;

            const isSelected = selectedCountry?.id === country.id;
            const isHovered = hoveredCountry === country.code;

            return (
              <div
                key={country.id}
                data-testid={`country-marker-${country.code}`}
                className="absolute cursor-pointer transition-all duration-200 pointer-events-auto"
                style={{
                  left: pos.left,
                  top: pos.top,
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => handleCountryClick(country)}
                onMouseEnter={() => {
                  setHoveredCountry(country.code);
                  // Play hover boop sound
                  const boop = new Audio('/assets/audio/hover-boop.mp3');
                  boop.volume = 0.3;
                  boop.play().catch(e => console.log('Hover boop failed:', e));
                }}
                onMouseLeave={() => {
                  setHoveredCountry(null);
                }}
              >
                {/* Country Territory Circle (placeholder) */}
                <div
                  className="relative"
                  style={{
                    width: isSelected ? '180px' : isHovered ? '160px' : '140px',
                    height: isSelected ? '180px' : isHovered ? '160px' : '140px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {/* Territory Base */}
                  <div
                    className="absolute inset-0 rounded-full opacity-40"
                    style={{
                      backgroundColor: pos.color,
                      boxShadow: isSelected 
                        ? `0 0 40px ${pos.color}, 0 0 80px ${pos.color}80` 
                        : isHovered 
                        ? `0 0 30px ${pos.color}` 
                        : 'none',
                    }}
                  />
                  
                  {/* Active Player Indicator - Green Glow (for ANY country with actions) */}
                  {country.actions_remaining > 0 && (
                    <div
                      className="absolute inset-0 rounded-full border-4 border-green-500 animate-pulse"
                      style={{
                        boxShadow: '0 0 30px rgba(34, 197, 94, 0.8), 0 0 60px rgba(34, 197, 94, 0.4)',
                      }}
                    />
                  )}
                  
                  {/* ☢️ Radiation glow effect - pulsing red/orange for irradiated countries */}
                  {country.radiation_damage?.active && (
                    <div className="group/radiation">
                      <div
                        className="absolute inset-0 rounded-full animate-pulse cursor-help"
                        style={{
                          background: 'radial-gradient(circle, rgba(255, 69, 0, 0.6) 0%, rgba(255, 140, 0, 0.4) 50%, transparent 70%)',
                          width: '120%',
                          height: '120%',
                          left: '-10%',
                          top: '-10%',
                          boxShadow: '0 0 60px rgba(255, 69, 0, 0.9), 0 0 100px rgba(255, 140, 0, 0.6)',
                        }}
                      />
                      
                      {/* Radiation Tooltip - Shows on hover */}
                      <div className="absolute left-1/2 top-full mt-4 -translate-x-1/2 opacity-0 group-hover/radiation:opacity-100 transition-opacity duration-200 z-50 pointer-events-none">
                        <div className="bg-gradient-to-br from-red-950 to-orange-950 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-orange-500/50 min-w-[240px]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">☢️</span>
                            <div>
                              <div className="font-bold text-orange-400">RADIATION FALLOUT</div>
                              <div className="text-xs text-orange-300/70">Active Nuclear Contamination</div>
                            </div>
                          </div>
                          <div className="border-t border-orange-800/50 pt-2 mt-2 space-y-1 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300">Duration:</span>
                              <span className="text-orange-400 font-mono font-bold">
                                {country.radiation_damage.turns_remaining} turn{country.radiation_damage.turns_remaining !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300">Pop. loss/turn:</span>
                              <span className="text-red-400 font-mono">-{country.radiation_damage.population_loss}M</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300">Money loss/turn:</span>
                              <span className="text-yellow-400 font-mono">-${country.radiation_damage.money_loss}M</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300">Total deaths:</span>
                              <span className="text-red-500 font-mono font-bold">
                                {(country.radiation_damage.turns_remaining * country.radiation_damage.population_loss).toFixed(1)}M
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Pulsing effect when selected */}
                  {isSelected && (
                    <div
                      className="absolute inset-0 rounded-full animate-ping opacity-20"
                      style={{ backgroundColor: pos.color }}
                    />
                  )}

                  {/* Country Label */}
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="text-center">
                      <div className="text-white font-bold text-xl mb-1 drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                        {country.code}
                      </div>
                      
                      {/* Action dots - 3 dots showing actions remaining */}
                      <div className="flex gap-1 justify-center mb-1">
                        {[1, 2, 3].map((dot) => {
                          // Fix: Properly handle actions_remaining (0 for AI, 3 for player)
                          const actionsRemaining = country.actions_remaining !== undefined ? country.actions_remaining : 0;
                          const actionsUsed = 3 - actionsRemaining;
                          const isUsed = dot <= actionsUsed;
                          return (
                            <div
                              key={dot}
                              className="w-2 h-2 rounded-full transition-all duration-300"
                              style={{
                                backgroundColor: isUsed ? '#ef4444' : '#22c55e',
                                boxShadow: isUsed 
                                  ? '0 0 4px rgba(239, 68, 68, 0.8)' 
                                  : '0 0 4px rgba(34, 197, 94, 0.8)',
                              }}
                            />
                          );
                        })}
                      </div>
                      
                      {/* Active Player Prompt (for ANY country with actions) */}
                      {country.actions_remaining > 0 && (
                        <div className="text-green-400 text-xs font-bold animate-pulse mt-1 drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
                          {country.is_player ? 'YOUR TURN' : 'THEIR TURN'}
                        </div>
                      )}
                      
                      {/* ☢️ Radiation warning badge */}
                      {country.radiation_damage?.active && (
                        <div className="mt-2 bg-red-600 px-2 py-1 rounded-full text-xs font-bold text-white animate-pulse inline-flex items-center gap-1 shadow-lg" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>
                          <span>☢️</span>
                          <span>RADIATION</span>
                          <span className="text-yellow-300">({country.radiation_damage.turns_remaining})</span>
                        </div>
                      )}
                      
                      <div className="text-white/60 text-xs drop-shadow-lg" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                        Click to select
                      </div>
                    </div>
                  </div>

                  {/* Leader Character - Hide if: 1) doing propaganda animation OR 2) hiding from incoming nuke! */}
                  {!(propagandaAnimation && propagandaAnimation.source === country.code) && 
                   !(hiddenLeaderCode === country.code) && (
                    <div 
                      className="absolute -top-32 left-1/2 transform -translate-x-1/2"
                      style={{
                        width: '180px',
                        height: '180px',
                        opacity: isSelected || isHovered ? 1 : 0.9,
                        transition: 'all 0.3s ease-in-out',
                        filter: isSelected ? 'drop-shadow(0 0 25px rgba(255,215,0,1))' : 'drop-shadow(0 6px 12px rgba(0,0,0,0.7))',
                        transform: `translate(calc(-50% + ${(leaderOffsets[country.code]?.x || 0) + (country.code === 'USA' ? -10 : 0)}px), ${leaderOffsets[country.code]?.y || 0}px) scale(${isHovered ? 1.15 : 1})`,
                        zIndex: 10,
                      }}
                    >
                      <img
                        src={getCountrySprite(country.code, countryEmotions[country.code])}
                        alt={`${country.name} Leader`}
                        className="w-full h-full object-contain transition-all duration-300"
                        style={{
                          imageRendering: 'auto',
                        }}
                      />

                      {/* 🎓 ONBOARDING ARROW — guides new players on turn 1-2.
                          Always mount at 3 o'clock relative to the LEADER
                          (not the screen). The leader sprites sit ~308px
                          above their country position, so a top-mounted
                          arrow gets cropped above the viewport. The right-
                          mounted arrow stays next to the leader's face
                          and is visible whenever the leader is. */}
                      {showOnboarding && (
                        <LeaderArrow
                          relationship={getRelationship(playerCode, country.code)}
                          placement="right"
                        />
                      )}
                      {/* ⭐ VICTORY STARS - Show gold stars for ANY country that eliminated enemies */}
                      {country.victory_stars > 0 && (
                        <div 
                          className="absolute -top-12 left-1/2 transform -translate-x-1/2 flex gap-1 items-center"
                          style={{
                            zIndex: 20,
                            filter: 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.8))',
                          }}
                        >
                          {[...Array(country.victory_stars)].map((_, index) => (
                            <div
                              key={`victory-star-${country.code}-${index}`}
                              className="animate-pulse"
                              style={{
                                fontSize: '1.25rem',  // Half size (was 2.5rem)
                                animation: `pulse 2s ease-in-out infinite ${index * 0.3}s`,
                                textShadow: '0 0 10px rgba(255, 215, 0, 1), 0 0 20px rgba(255, 215, 0, 0.6)',  // Smaller glow
                              }}
                            >
                              ⭐
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* POPULATION COUNTER - Critical resource tracker - Country-specific positioning */}
                      <div 
                        className="absolute cursor-help"
                        style={{
                          // Country-specific positioning to avoid obstructions
                          ...(country.code === 'CHN'
                            ? { bottom: '-145px', left: '50%', transform: 'translateX(-50%)' }  // SOUTH (1/2 inch more south)
                            : country.code === 'NK'
                            ? { top: '65%', right: '-80px', transform: 'translateY(-50%)' }  // ⭐ MOVED DOWN to avoid Nuclear Threat panel
                            : country.code === 'RUS'
                            ? { top: '5px', right: '-15px' }  // NORTHEAST but further southwest
                            : { bottom: '-60px', left: '-60px' }  // SOUTHWEST (default for USA, EU)
                          ),
                          zIndex: 15,
                        }}
                        title={`${country.name} Population: ${country.resources.population}M`}
                      >
                        <div className="flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all duration-200 hover:scale-110"
                          style={{
                            backgroundColor: 'rgba(15, 23, 42, 0.95)',
                            borderColor: country.resources.population <= 10 ? '#ef4444' : country.resources.population <= 30 ? '#f59e0b' : pos.color,
                            boxShadow: country.resources.population <= 10 
                              ? '0 0 20px rgba(239, 68, 68, 0.6)' 
                              : `0 0 12px ${pos.color}40`,
                          }}
                        >
                          {/* Population Icon */}
                          <div className="text-xl" style={{
                            filter: country.resources.population <= 10 ? 'grayscale(50%)' : 'none',
                          }}>
                            👥
                          </div>
                          
                          {/* Population Count - ANIMATED and COLORED to match country */}
                          <AnimatedPopulation 
                            value={country.resources.population}
                            color={pos.color}
                            isLow={country.resources.population <= 10}
                            airStrikeActive={airStrikeActive}
                            nuclearStrikeActive={nuclearStrikeActive}
                          />
                          
                          {/* Country Code Label */}
                          <div className="text-xs text-white/70 uppercase tracking-wider font-bold">
                            {country.code}
                          </div>
                          
                          {/* Low Population Warning */}
                          {country.resources.population <= 10 && (
                            <div className="text-xs text-red-400 font-bold animate-pulse mt-1">
                              ⚠️ LOW
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Sweat drops when neighboring country is nuked */}
                      {sweatingLeaders && sweatingLeaders.includes(country.code) && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          {/* Multiple sweat drops with staggered animations */}
                          <div className="sweat-drop-1" style={{
                            position: 'absolute',
                            top: '25%',
                            right: '35%',
                            fontSize: '2rem',
                            animation: 'sweat 1.5s ease-in-out infinite'
                          }}>💧</div>
                          <div className="sweat-drop-2" style={{
                            position: 'absolute',
                            top: '30%',
                            right: '25%',
                            fontSize: '1.5rem',
                            animation: 'sweat 1.5s ease-in-out infinite 0.3s'
                          }}>💧</div>
                          <div className="sweat-drop-3" style={{
                            position: 'absolute',
                            top: '35%',
                            right: '40%',
                            fontSize: '1.8rem',
                            animation: 'sweat 1.5s ease-in-out infinite 0.6s'
                          }}>💧</div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* ⚡ AIR DEFENSE INDICATOR - Custom AA sprites with directional facing */}
                  {country.air_defense_level > 0 && (
                    <div 
                      className="absolute"
                      style={{
                        // USA: 1 o'clock (top-right) - less busy, actual USA land, clear of leader
                        // Others: 5 o'clock (bottom-right)
                        ...(country.code === 'USA' 
                          ? { top: '-60px', right: '-27px', zIndex: 15 }  // More northeast, clears leader perfectly
                          : { bottom: '-48px', right: '0px', zIndex: 15 }  // 5 o'clock
                        )
                      }}
                    >
                      {/* Determine if AA should face left (right-side countries) */}
                      {(() => {
                        const rightSideCountries = ['CHN', 'NK', 'RUS']; // Countries on right side of map - point guns left
                        const shouldFlip = rightSideCountries.includes(country.code);
                        const aaImages = {
                          1: '/assets/aa-tier-1.png',
                          2: '/assets/aa-tier-2.png',
                          3: '/assets/aa-tier-3.png'
                        };
                        
                        return (
                          <div className="relative">
                            <img 
                              src={aaImages[country.air_defense_level]}
                              alt={`AA Defense Tier ${country.air_defense_level}`}
                              className="transition-transform hover:scale-110"
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'contain',
                                transform: shouldFlip ? 'scaleX(-1)' : 'none',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                              }}
                              title={`Air Defense Tier ${country.air_defense_level}: +${[0, 15, 25, 35][country.air_defense_level]}% defense against air strikes`}
                            />
                            {/* AA Tier Badge */}
                            <div 
                              className="absolute -bottom-1 -right-1 bg-blue-900/90 px-1.5 py-0.5 rounded border border-blue-500 text-xs text-blue-300 font-bold"
                              style={{ fontSize: '0.6rem' }}
                            >
                              T{country.air_defense_level}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* 🛰️ RADAR DISH INDICATOR - Smooth frame-by-frame animation */}
                  {country.has_radar && (
                    <div 
                      className="absolute"
                      style={{
                        // Smart positioning per country to avoid overlaps
                        // USA: 11 o'clock (top-left) - clears AA at 1 o'clock
                        // RUS/CHN/NK: 7 o'clock (bottom-left) - clears AA at 5 o'clock, clears population window
                        // EU: 9 o'clock (left) - clears AA at 5 o'clock
                        ...(country.code === 'USA' 
                          ? { top: '-60px', left: '-30px', zIndex: 15 }  // 11 o'clock
                          : country.code === 'EU'
                          ? { top: '0px', left: '-55px', zIndex: 15 }    // 9 o'clock (left side)
                          : { bottom: '-50px', left: '-10px', zIndex: 15 }  // 7 o'clock (bottom-left) for RUS/CHN/NK
                        )
                      }}
                    >
                      <div className="relative">
                        <div
                          className="radar-dish-animated"
                          style={{
                            width: '48px',
                            height: '48px',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                          }}
                          title="🛰️ Radar Installed: Auto intel each turn (15% destruction chance per air strike)"
                        />
                        {/* Radar Active Badge */}
                        <div 
                          className="absolute -bottom-1 -right-1 bg-green-900/90 px-1.5 py-0.5 rounded border border-green-500 text-xs text-green-300 font-bold animate-pulse"
                          style={{ fontSize: '0.55rem' }}
                        >
                          📡
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Nuke Ready Indicator - Show nuke sprite if country has nukes */}
                  {country.resources?.nukes_ready > 0 && !(propagandaAnimation && propagandaAnimation.source === country.code) && (
                    <div 
                      className="absolute -top-32 left-1/2"
                      style={{
                        width: '80px',
                        height: '80px',
                        opacity: 1,
                        transition: 'all 0.3s ease-in-out',
                        filter: 'drop-shadow(0 4px 8px rgba(255,0,0,0.8))',
                        transform: `
                          translate(calc(-50% + ${nukeOffsets[country.code]?.x || 0}px), ${nukeOffsets[country.code]?.y || 0}px) 
                          scale(${isHovered ? 1.15 : 1})
                          scale(${country.code === 'USA' ? 0.75 : country.code === 'EU' ? 1.25 : 1})
                        `,
                        zIndex: 30, // High z-index to ensure it's in foreground
                      }}
                    >
                      <img
                        src={`/assets/nukes/nuke-${
                          country.code === 'USA' ? 'usa' :
                          country.code === 'RUS' ? 'russia' :
                          country.code === 'CHN' ? 'china' :
                          country.code === 'EU' ? 'eu' :
                          country.code === 'NK' ? 'nkorea' : 'usa'
                        }.png`}
                        alt=""
                        className="w-full h-full object-contain transition-all duration-300 animate-pulse"
                        style={{
                          imageRendering: 'auto',
                        }}
                        onError={(e) => {
                          console.error(`Failed to load nuke sprite for ${country.code}:`, e.target.src);
                        }}
                      />
                      {/* Nuke count badge */}
                      {country.resources.nukes_ready > 1 && (
                        <div 
                          className="absolute -bottom-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white"
                          style={{
                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                          }}
                        >
                          {country.resources.nukes_ready}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* LANDMARKS - Famous landmarks that degrade with population loss */}
          {countries && countries
            .filter(country => !defeatedCountries.includes(country.code))
            .map((country) => {
              // Get initial population from game data (turn 1 snapshot)
              const initialPopulation = game?.initial_populations?.[country.code] || country.resources?.population || 100;
              
              return (
                <Landmark 
                  key={`landmark-${country.code}`}
                  country={country}
                  initialPopulation={initialPopulation}
                />
              );
            })}
          
          {/* DEPLOYED TROOPS - Show army graphics stacked next to each other */}
          {game && game.active_deployments && game.active_deployments.map((deployment, deploymentIndex) => {
            // Find source and target countries
            const sourceCountry = countries.find(c => c.code === deployment.source);
            const targetCountry = countries.find(c => c.code === deployment.target);
            
            if (!sourceCountry || !targetCountry) return null;
            
            const targetPos = countryPositions[deployment.target];
            if (!targetPos) return null;
            
            // Get army sprite filename
            const armyMap = {
              'USA': 'usa-army.png',
              'RUS': 'russia-army.png',
              'CHN': 'china-army.png',
              'EU': 'europe-army.png',
              'NK': 'nkorea-army.png'
            };
            const armySprite = armyMap[deployment.source];
            
            console.log(`🪖 ARMY DEBUG: deployment.source = "${deployment.source}", armySprite = "${armySprite}"`);
            
            if (!armySprite) return null;
            
            // Calculate how many armies are on this same target (for stacking)
            const deploymentsToThisTarget = game.active_deployments.filter(d => d.target === deployment.target);
            const indexOnThisTarget = deploymentsToThisTarget.findIndex(d => d.source === deployment.source);
            const totalOnTarget = deploymentsToThisTarget.length;
            
            // Parse position percentages (e.g., "65%" to 65)
            const leftPercent = parseFloat(targetPos.left);
            const topPercent = parseFloat(targetPos.top);
            
            // Smart stacking: Arrange armies in a row next to each other
            // If 1 army: center below country
            // If 2 armies: left and right
            // If 3+ armies: spread out in a row
            let offsetX = 0;
            if (totalOnTarget === 1) {
              offsetX = 0; // Center
            } else if (totalOnTarget === 2) {
              offsetX = indexOnThisTarget === 0 ? -7 : 7; // Left and right
            } else if (totalOnTarget === 3) {
              offsetX = (indexOnThisTarget - 1) * 7; // -7, 0, +7
            } else {
              // 4+ armies: spread wider
              const spacing = 6;
              const centerOffset = ((totalOnTarget - 1) * spacing) / 2;
              offsetX = (indexOnThisTarget * spacing) - centerOffset;
            }
            
            // Position army BELOW the target country marker with horizontal offset
            const armyLeft = leftPercent + offsetX;
            const armyTop = topPercent + 8;
            
            return (
              <div
                key={`army-${deployment.source}-to-${deployment.target}-${deploymentIndex}`}
                style={{
                  position: 'absolute',
                  left: `${armyLeft}%`,
                  top: `${armyTop}%`,
                  width: '12%',
                  transform: 'translate(-50%, 0)',
                  height: 'auto',
                  zIndex: 15 + indexOnThisTarget, // Layer them slightly
                  transition: 'all 0.3s ease',
                  pointerEvents: 'none'
                }}
              >
                <img
                  src={`/assets/troops/${armySprite}`}
                  alt={`${sourceCountry.name} troops in ${targetCountry.name} (${deployment.rounds_remaining} rounds left)`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))'
                  }}
                />
                {/* Rounds remaining indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(220, 38, 38, 0.9)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                  {deployment.rounds_remaining} {deployment.rounds_remaining === 1 ? 'ROUND' : 'ROUNDS'}
                </div>
              </div>
            );
          })}
        </div>
        {/* 📱 END MOBILE AUTO-SCALE WRAPPER */}
      </div>

      {/* ☠️ DEFEATED COUNTRIES - Show skull markers for eliminated nations */}
      {/* ⭐ HIDE skull during nuclear strike animation for dramatic effect */}
      <div 
        className="absolute bottom-0 left-0 transition-transform duration-1000 ease-out pointer-events-none"
        style={{
          top: isMobilePortrait ? '80px' : '0',
          width: isMobilePortrait ? '200%' : '100%',
          minWidth: isMobilePortrait ? '200%' : undefined,
          // On mobile portrait, don't apply mapEntityScale here — it has a
          // center-center origin and would shift skull markers off their
          // country positions on the map. Keep desktop/tablet scaling intact.
          transform: isMobilePortrait
            ? `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom})`
            : `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom * mapEntityScale})`
        }}
      >
        {countries && countries
          .filter(country => country.eliminated || country.is_eliminated)
          .filter(country => {
            // ⭐ HIDE skull if this country is being nuked (let the mushroom cloud reveal the defeat!)
            if (nuclearStrikeActive && nuclearStrikeTargetCode === country.code) {
              return false; // Don't show skull during nuke animation
            }
            return true;
          })
          .map((country) => {
            const pos = countryPositions[country.code];
            if (!pos) return null;

            return (
              <div
                key={`defeated-${country.code}`}
                className="pointer-events-auto cursor-help transition-all duration-200 group absolute"
                style={{
                  left: pos.left,
                  top: pos.top,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {/* Defeated Circle Marker */}
                <div className="relative">
                  {/* Dark outer glow */}
                  <div
                    className="absolute inset-0 w-24 h-24 rounded-full opacity-40 blur-xl"
                    style={{ backgroundColor: '#7f1d1d' }}
                  />
                  
                  {/* Main circle - dark red/black */}
                  <div
                    className="relative w-16 h-16 rounded-full border-2 flex items-center justify-center"
                    style={{
                      backgroundColor: 'rgba(127, 29, 29, 0.3)',
                      borderColor: 'rgba(127, 29, 29, 0.8)',
                      boxShadow: '0 0 20px rgba(127, 29, 29, 0.6)',
                    }}
                  >
                    {/* Skull emoji */}
                    <div className="text-3xl mb-0 opacity-80 animate-pulse">💀</div>
                  </div>

                  {/* Country code label */}
                  <div className="absolute inset-0 flex items-center justify-center z-20 mt-20">
                    <div className="text-center bg-black/90 px-2 py-1 rounded-lg border border-red-900">
                      <div className="text-red-400/80 font-bold text-xs">
                        {country.code}
                      </div>
                      <div className="text-red-500/60 text-[9px] uppercase tracking-wider">
                        Defeated
                      </div>
                    </div>
                  </div>

                  {/* Tooltip on hover - shows defeat details */}
                  <div className="absolute left-1/2 top-full mt-6 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30">
                    <div className="bg-gradient-to-br from-slate-900 to-red-950 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-red-500/50 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">💀</span>
                        <div>
                          <div className="font-bold text-red-400">{country.name}</div>
                          <div className="text-xs text-red-300/70">ELIMINATED</div>
                        </div>
                      </div>
                      <div className="border-t border-red-800/50 pt-2 mt-2 space-y-1 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Defeated on:</span>
                          <span className="text-white font-mono">Turn {country.defeated_on_turn || country.eliminated_turn || '?'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Deathblow by:</span>
                          <span className="text-red-400 font-bold">{country.eliminated_by_name || country.eliminated_by || 'Unknown'}</span>
                        </div>
                        {country.elimination_reason && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Cause:</span>
                            <span className="text-red-300 text-xs capitalize">{country.elimination_reason.replace('_', ' ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Locked Factions - Coming Soon!
          See `showLockedLandmarks` derivation above for visibility rules. */}
      {showLockedLandmarks && (
      <div 
        className="absolute bottom-0 left-0 transition-transform duration-1000 ease-out pointer-events-none"
        style={{
          top: isMobilePortrait ? '80px' : '0',
          width: isMobilePortrait ? '200%' : '100%',
          minWidth: isMobilePortrait ? '200%' : undefined,
          transform: `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom})`
        }}
      >
        {[
          // Middle East
          { code: 'IRN', name: 'Iran', flag: '🇮🇷', left: '65%', top: '38%', color: '#10b981' },  // Moved right for readability
          { code: 'ISR', name: 'Israel', flag: '🇮🇱', left: '57%', top: '37%', color: '#06b6d4' },
          // Latin America
          { code: 'VEN', name: 'Venezuela', flag: '🇻🇪', left: '28%', top: '52%', color: '#eab308' },
          { code: 'MEX', name: 'Mexico', flag: '🇲🇽', left: '15%', top: '42%', color: '#f97316' }
        ].map((faction) => (
          <div
            key={faction.code}
            className="pointer-events-auto transition-all duration-200 absolute"
            style={{
              left: faction.left,
              top: faction.top,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {/* Locked Circle Marker - Larger hover area */}
            <div className="relative group cursor-pointer p-8 -m-8 hover:z-50">
              {/* Outer glow */}
              <div
                className="absolute inset-0 w-16 h-16 rounded-full opacity-30 blur-xl pointer-events-none"
                style={{ backgroundColor: faction.color }}
              />
              
              {/* Main circle - bigger and reactive */}
              <div
                className="relative w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all duration-200 group-hover:scale-110 group-hover:shadow-2xl"
                style={{
                  backgroundColor: `${faction.color}20`,
                  borderColor: `${faction.color}80`,
                  boxShadow: `0 0 15px ${faction.color}40`,
                }}
              >
                {/* Flag emoji - smaller */}
                <div className="text-xl mb-0 opacity-50">{faction.flag}</div>
                
                {/* Lock icon overlay - smaller */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                  <div className="text-white text-xl">🔒</div>
                </div>
              </div>

              {/* Country code label - smaller spacing - Don't block hover */}
              <div className="absolute inset-0 flex items-center justify-center z-20 mt-16 pointer-events-none">
                <div className="text-center bg-black/80 px-2 py-0.5 rounded-lg border border-gray-600">
                  <div className="text-white/60 font-bold text-xs mb-0">
                    {faction.code}
                  </div>
                  <div className="text-amber-400 text-[10px] uppercase tracking-wider animate-pulse">
                    Coming Soon
                  </div>
                </div>
              </div>

              {/* Tooltip on hover - Position based on location to avoid UI overlaps */}
              <div 
                className={`absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 pointer-events-none
                  ${faction.code === 'MEX' 
                    ? 'left-full top-1/2 ml-4 -translate-y-1/2'  // Mexico: Right side (avoid Intelligence Feed)
                    : 'left-1/2 top-full mt-4 -translate-x-1/2'  // Others: Below
                  }`}
              >
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-amber-500/50 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-3xl">{faction.flag}</span>
                    <div>
                      <div className="font-bold text-lg">{faction.name}</div>
                      <div className="text-xs text-gray-400 uppercase">
                        {faction.subtitle || 'Locked Faction'}
                      </div>
                    </div>
                  </div>
                  {faction.code === 'ZA' ? (
                    <>
                      <div className="text-sm text-emerald-300 font-semibold mb-2">
                        🚀 The Tech Oligarch
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        <div>• Private tech empire</div>
                        <div>• Drone swarm tactics</div>
                        <div>• Railgun AA defenses</div>
                        <div>• Orbital strike platform</div>
                        <div>• Rapid deployment ability</div>
                      </div>
                    </>
                  ) : faction.code === 'VEN' ? (
                    <>
                      <div className="text-sm text-amber-300 font-semibold mb-2">
                        ⚡ The Oil Titan
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        <div>• Resource-based warfare</div>
                        <div>• Economic sanctions power</div>
                        <div>• Oil embargo tactics</div>
                        <div>• Energy crisis manipulation</div>
                        <div>• Regional influence control</div>
                      </div>
                    </>
                  ) : faction.code === 'IRN' ? (
                    <>
                      <div className="text-sm text-emerald-300 font-semibold mb-2">
                        🏺 Ancient Civilizations
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        <div>• Never-seen-before cluster munitions</div>
                        <div>• Advanced drone warfare</div>
                        <div>• Swarm attack formations</div>
                        <div>• Precision strike capabilities</div>
                        <div>• Ancient strategy, modern tech</div>
                      </div>
                    </>
                  ) : faction.code === 'ISR' ? (
                    <>
                      <div className="text-sm text-cyan-300 font-semibold mb-2">
                        🕵️ Master of Blackmail
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        <div>• Covert world control</div>
                        <div>• Intelligence network mastery</div>
                        <div>• Blackmail & leverage tactics</div>
                        <div>• Shadow operations</div>
                        <div>• Information warfare supremacy</div>
                      </div>
                    </>
                  ) : faction.code === 'MEX' ? (
                    <>
                      <div className="text-sm text-orange-300 font-semibold mb-2">
                        🤝 The Strategic Ally
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        <div>• Works intently with allies</div>
                        <div>• Border security specialist</div>
                        <div>• Cartel warfare expertise</div>
                        <div>• Strategic partnerships</div>
                        <div>• Bridge between continents</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-amber-300 font-semibold">
                        🔓 Unlockable in future update!
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Stay tuned for new strategic options
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* 🚢 STRATEGIC LOCATIONS - Strait of Hormuz */}
      {showLockedLandmarks && (
      <div 
        className="absolute bottom-0 left-0 transition-transform duration-1000 ease-out pointer-events-none"
        style={{
          top: isMobilePortrait ? '80px' : '0',
          width: isMobilePortrait ? '200%' : '100%',
          minWidth: isMobilePortrait ? '200%' : undefined,
          transform: `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom})`
        }}
      >
        {/* Strait of Hormuz - Strategic Chokepoint */}
        <div
          className="pointer-events-auto cursor-pointer transition-all duration-200 group absolute"
          style={{
            left: '60.5%',  // Between Iran and Oman (Persian Gulf entrance)
            top: '40%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {/* Locked Strategic Location Marker */}
          <div className="relative group p-8 -m-8">
            {/* Outer glow - cyan for water/strategic route */}
            <div
              className="absolute inset-0 w-14 h-14 rounded-lg opacity-30 blur-xl pointer-events-none"
              style={{ backgroundColor: '#06b6d4' }}
            />
            
            {/* Main marker - rectangular for strait/corridor */}
            <div
              className="relative w-14 h-14 rounded-lg border-2 flex items-center justify-center transition-all duration-200 group-hover:scale-110 group-hover:shadow-2xl"
              style={{
                backgroundColor: '#06b6d420',
                borderColor: '#06b6d480',
                boxShadow: '0 0 15px #06b6d440',
              }}
            >
              {/* Icon: Ship/Tanker emoji */}
              <div className="text-2xl mb-0 opacity-60">🚢</div>
              
              {/* Lock icon overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                <div className="text-white text-xl">🔒</div>
              </div>
            </div>

            {/* Location label - Moved down to avoid covering Iran */}
            <div className="absolute inset-0 flex items-center justify-center z-20 mt-20 pointer-events-none">
              <div className="text-center bg-black/80 px-2 py-0.5 rounded-lg border border-cyan-600">
                <div className="text-cyan-400/80 font-bold text-[10px] mb-0 whitespace-nowrap">
                  HORMUZ
                </div>
                <div className="text-green-400 text-[9px] uppercase tracking-wider">
                  Currently Open
                </div>
              </div>
            </div>

            {/* Tooltip on hover */}
            <div 
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 pointer-events-none left-1/2 top-full mt-4 -translate-x-1/2"
            >
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-cyan-500/50 min-w-[220px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-3xl">🚢</span>
                  <div>
                    <div className="font-bold text-base">Strait of Hormuz</div>
                    <div className="text-xs text-gray-400 uppercase">
                      Strategic Corridor
                    </div>
                  </div>
                </div>
                
                {/* Status */}
                <div className="text-sm text-green-300 font-semibold mb-2 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Currently Open
                </div>
                
                {/* Strategic info */}
                <div className="text-xs text-gray-300 space-y-1 mb-2">
                  <div>• 21% of global oil passes through</div>
                  <div>• 21 miles wide at narrowest point</div>
                  <div>• Critical chokepoint for energy</div>
                  <div>• Links Persian Gulf to Arabian Sea</div>
                </div>

                {/* Lock info */}
                <div className="text-xs text-amber-300 mt-2 pt-2 border-t border-gray-700">
                  <div className="font-semibold mb-1">🔒 May be closed once Iran is unlocked</div>
                  <div className="text-gray-400">
                    Iran can block the strait as a strategic action
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 🇿🇦 SOUTH AFRICA - Fresh Implementation */}
      {showLockedLandmarks && (
      <div 
        className="absolute bottom-0 left-0 transition-transform duration-1000 ease-out pointer-events-none"
        style={{
          top: isMobilePortrait ? '80px' : '0',
          width: isMobilePortrait ? '200%' : '100%',
          minWidth: isMobilePortrait ? '200%' : undefined,
          transform: `translate(${cameraPan.x}%, ${cameraPan.y}%) scale(${cameraZoom})`
        }}
      >
        <div
          className="pointer-events-auto absolute"
          style={{
            left: '52%',
            top: '68%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="relative">
            {/* ✅ EXPLICIT HOVER TARGET - Larger area */}
            <div className="group cursor-pointer p-8 -m-8 relative hover:z-50">
              {/* Glow */}
              <div
                className="absolute inset-0 w-16 h-16 rounded-full opacity-30 blur-xl pointer-events-none left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ backgroundColor: '#10b981' }}
              />
              
              {/* Main circle */}
              <div
                className="relative w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all duration-200 group-hover:scale-110 group-hover:shadow-2xl mx-auto"
                style={{
                  backgroundColor: '#10b98120',
                  borderColor: '#10b98180',
                  boxShadow: '0 0 20px #10b98140',
                }}
              >
                <div className="text-3xl mb-0">🇿🇦</div>
                
                {/* Lock icon overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                  <div className="text-white text-2xl">🔒</div>
                </div>
              </div>

              {/* Country code label - Match other locked countries exactly */}
              <div className="absolute inset-0 flex items-center justify-center z-20 mt-16 pointer-events-none">
                <div className="text-center bg-black/80 px-2 py-0.5 rounded-lg border border-gray-600">
                  <div className="text-white/60 font-bold text-xs mb-0">
                    ZA
                  </div>
                  <div className="text-amber-400 text-[10px] uppercase tracking-wider animate-pulse">
                    Coming Soon
                  </div>
                </div>
              </div>

              {/* Tooltip - 3 o'clock position (right side) */}
              <div className="absolute left-full top-1/2 ml-4 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 pointer-events-none">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl border-2 border-green-500/50 min-w-[240px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-3xl">🇿🇦</span>
                    <div>
                      <div className="font-bold text-lg">South Africa</div>
                      <div className="text-xs text-gray-400 uppercase">Coming Soon</div>
                    </div>
                  </div>
                  
                  {/* Match the exact style of other countries */}
                  <div className="text-sm text-green-300 font-semibold mb-2">
                    💼 The Tech Oligarch
                  </div>
                  <div className="text-xs text-gray-300 space-y-1">
                    <div>• AI-driven cyber warfare</div>
                    <div>• Tech monopoly control</div>
                    <div>• Resource extraction empire</div>
                    <div>• Satellite surveillance network</div>
                    <div>• Digital warfare superiority</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Selection Indicator */}
      {selectedCountry && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black/80 px-6 py-3 rounded-lg border border-purple-500">
          <p className="text-purple-300 text-center">
            Selected: <span className="text-white font-bold">{selectedCountry.name}</span>
          </p>
        </div>
      )}
      
      {/* Propaganda Animation Overlay */}
      {propagandaAnimation && (
        <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
          {propagandaAnimation.phase === 'propaganda' && (
            <div className={`propaganda-animation ${shouldFlipPropaganda(propagandaAnimation.source, propagandaAnimation.target) ? 'scale-x-[-1]' : ''}`}>
              <img
                src={getPropagandaSprite(propagandaAnimation.source)}
                alt="Propaganda"
                style={{
                  width: '400px',
                  height: '400px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.8))',
                }}
              />
            </div>
          )}
          {propagandaAnimation.phase === 'celebration' && (
            <div className="victory-dance-animation">
              <img
                src={getCountrySprite(propagandaAnimation.source, 'happy')}
                alt="Victory"
                style={{
                  width: '400px',
                  height: '400px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 30px rgba(255,215,0,0.9)) brightness(1.2) saturate(1.3)',
                }}
              />
            </div>
          )}
        </div>
      )}
      
      {/* CSS for sweat drop animation */}
      <style jsx>{`
        @keyframes sweat {
          0% {
            opacity: 0;
            transform: translateY(0px);
          }
          20% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(40px);
          }
        }
        
        @keyframes pulse-red {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.3); filter: brightness(1.5) drop-shadow(0 0 10px #ef4444); }
        }
        
        @keyframes pulse-green {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.3); filter: brightness(1.5) drop-shadow(0 0 10px #22c55e); }
        }
        
        /* 🛰️ RADAR DISH ANIMATION - 4 Frame Rotation (Radar2 → Radar3 → Radar8 → Radar7) */
        @keyframes radarRotate {
          0%   { background-image: url('/assets/radar/radar2.png'); }
          25%  { background-image: url('/assets/radar/radar3.png'); }
          50%  { background-image: url('/assets/radar/radar8.png'); }
          75%  { background-image: url('/assets/radar/radar7.png'); }
          100% { background-image: url('/assets/radar/radar2.png'); }
        }
        
        .radar-dish-animated {
          animation: radarRotate 2s steps(1) infinite;
        }
      `}</style>
      </div>
    </>
  );
}

